import React, { useState, useRef } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCopy, faRefresh } from '@fortawesome/free-solid-svg-icons'
import PrixIcon from '~/public/prix-icon.svg'

const BubbleContainer = styled.div<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  position: relative;
  /* max-width: 100%;
  overflow-x: hidden; */
`

const rotate = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`

const AvatarContainer = styled.div<{ $isThinking?: boolean }>`
  flex-shrink: 0;
  margin-right: 0.5rem;
  margin-top: 0.25rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  /* background: ${({ theme }) => theme.colors.primary}; */
  /* border: 2px solid #fff; */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 7px 10px 10px;
  position: relative;
  overflow: visible;
  z-index: 1;

  ${({ $isThinking }) =>
    $isThinking &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: calc(100% + 12px);
        height: calc(100% + 12px);
        border-left: 3px solid ${({ theme }) => theme.colors.primary};
        border-right: 3px solid transparent;
        border-top: 3px solid transparent;
        border-bottom: 3px solid transparent;
        border-radius: 50%;
        z-index: 0;
        animation: ${rotate} 2s linear infinite;
      }
    `}

  & > * {
    width: 100%;
    height: 100%;
  }

  @media screen and (min-width: 1081px) {
    position: absolute;
    left: -3.5rem;
    top: 0;
  }
`

const BubbleContent = styled.div<{ $isUser: boolean }>`
  position: relative;
  margin-left: 0;
  margin-right: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  flex: 1;
  max-width: 100%;
  overflow-x: hidden;
`

const BubbleBody = styled.div<{ $isUser: boolean }>`
  padding: 1rem 1.2rem;
  border-radius: 0.5rem;
  background-color: ${({ $isUser, theme }) => ($isUser ? theme.colors.primary : '#fff')};
  color: ${({ $isUser }) => ($isUser ? '#fff' : '#333')};
  border-bottom-right-radius: ${({ $isUser }) => ($isUser ? '0' : '0.5rem')};
  border-bottom-left-radius: ${({ $isUser }) => ($isUser ? '0.5rem' : '0')};
  transition: all 0.2s ease;
  box-sizing: border-box;
  max-width: 100%;
  line-height: 1.3;

  p,
  ol,
  ul {
    margin: 5px 0 20px;
  }
  li {
    margin: 0;
    margin-bottom: 10px;
  }
  pre,
  code {
    overflow-x: auto;
    max-width: 100%;
  }
  b,
  strong {
    font-weight: 600;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 5px 0 15px;
  }
  table {
    margin: 5px 0 15px;
    thead {
      background-color: ${({ theme }) => theme.colors.primary};
      color: #fff;
      th {
        padding: 5px 10px;
      }
    }
    tbody {
      tr {
        background-color: #e1e1e1;
        td {
          padding: 5px 10px;
        }
      }
    }
  }
  blockquote {
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
    padding-left: 10px;
    margin: 5px 0 15px;
  }

  h1 {
    font-size: 1.65rem;
  }
  h2 {
    font-size: 1.35rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  h4 {
    font-size: 1rem;
  }
  h5 {
    font-size: 0.85rem;
  }
  h6 {
    font-size: 0.7rem;
  }
  img {
    max-width: 100%;
    height: auto;
  }
`

const BubbleActions = styled.div<{ $isUser: boolean }>`
  transition: all 0.25s;
  display: flex;
  justify-content: ${({ $isUser }) => (!$isUser ? `start` : `end`)};
  align-items: center;
  flex-direction: ${({ $isUser }) => (!$isUser ? `row` : `row-reverse`)};
  gap: 3px;
`

const ActionButton = styled.button`
  padding: 0.25rem;
  color: ${({ theme }) => theme.colors.primary};
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: opacity 0.25s;
  opacity: 0.5;
  bottom: 0;
  font-size: 18px;

  &:hover {
    opacity: 1;
  }
`

interface MessageBubbleProps {
  isThinking?: boolean
  children: React.ReactNode
  type: 'user' | 'ai'
  onRegenerate?: () => void
  copyContent?: string
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  isThinking,
  children,
  type,
  onRegenerate,
  copyContent,
}) => {
  const [isCopied, setIsCopied] = useState(false)
  const bubbleRef = useRef<HTMLDivElement>(null)

  const copyToClipboard = () => {
    if (isCopied) return
    navigator.clipboard.writeText(copyContent ?? '')
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  const isUser = type === 'user'

  return (
    <>
      <BubbleContainer $isUser={isUser}>
        {!isUser && (
          <AvatarContainer $isThinking={isThinking}>
            <PrixIcon />
          </AvatarContainer>
        )}

        <BubbleContent $isUser={isUser}>
          <BubbleBody $isUser={isUser} ref={bubbleRef}>
            {children}
          </BubbleBody>

          <BubbleActions $isUser={isUser}>
            <ActionButton
              data-tooltip-id='tooltip-bubble-action'
              data-tooltip-content='Copiar'
              onClick={copyToClipboard}
              aria-label='Copiar'
            >
              <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
            </ActionButton>

            {onRegenerate ? (
              <ActionButton
                onClick={() => onRegenerate?.()}
                data-tooltip-id='tooltip-bubble-action'
                data-tooltip-content='Refazer'
                aria-label='Refazer'
              >
                <FontAwesomeIcon icon={faRefresh} />
              </ActionButton>
            ) : null}
          </BubbleActions>
        </BubbleContent>
      </BubbleContainer>
    </>
  )
}

export default React.memo(MessageBubble)
